import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import Accordion from '@nerdwallet/react-accordion';
import uuid from 'uuid/v4';
import { Box, CenteredContainer } from '@nerdwallet/currency';
import { ElementImpression } from '@nerdwallet/nw-client-lib/tracking';
import { getFaqSchema } from '@nerdwallet/json-ld/mappings';
import getTextColor from '../../lib/text-color-config';
import UniversalText from '../universal-text/universal-text';
import {
  boolCheckboxSpec,
  repeatable,
  group,
  wrapper,
} from '../../lib/editing-configs';
import { UniversalContainer } from '../../lib/component-lib';
import './universal-accordion.less';

const UniversalAccordion = ({
  universalAccordion,
  fullWidthMobile,
  strokeColorOptions,
  rollOverColorOptions,
  outputSchema,
  size,
  ...rest
}) => {
  const accordionRef = useRef();

  const container = universalAccordion?.container || rest.container;
  const title = universalAccordion?.title || rest.title;
  const subtitle = universalAccordion?.subtitle || rest.subtitle;
  const panels = universalAccordion?.panels || rest.panels;

  const nodeId = uuid();
  const [trkId] = useState(nodeId);

  const { textColor: strokeColorConfig } = getTextColor(strokeColorOptions);
  const { textColor: rollOverColorConfig } = getTextColor(rollOverColorOptions);
  const strokeColorStyle = { borderColor: strokeColorConfig };

  const mapped = panels?.map(({ header, body }) => ({
    answer: body,
    question: header,
  }));

  const faqSchema = getFaqSchema(mapped);
  const jsonLd = { '@context': 'http://schema.org', ...faqSchema };

  const elementImpressionProps = {
    elementContent: title,
    id: `universal-accordion-${trkId}`,
    elementDomType: 'div',
    elementName: 'Accordion',
    sectionCategory: 'UniversalAccordion',
  };

  const analytics = {
    enabled: true,
    payload: {
      elementName: 'landing_page_accordion_item',
    },
  };

  let mobileWidth;
  if (fullWidthMobile) {
    mobileWidth = '100%';
  } else {
    mobileWidth = '280px';
  }

  const containerWidthStyle = {
    width: size === 'sm' && `${mobileWidth}`,
  };

  const items = panels?.map(({ header, body }, key) => ({
    children: header ? (
      <UniversalText {...body} textType={header?.textType || 'text'} />
    ) : (
      ''
    ),
    title: header ? (
      <UniversalText {...header} textType={header?.textType || 'text'} />
    ) : (
      ''
    ),
    key,
  }));

  useEffect(() => {
    // because we want to apply different styling for each Accordion instance in the page
    if (accordionRef.current) {
      const accordionItems = accordionRef.current.querySelectorAll(
        '.editable-panel__wrapper .panel.panel-default'
      );

      const accordionFirstLine = accordionRef.current.querySelector(
        '.editable-panel__wrapper > div'
      );

      if (accordionFirstLine) {
        accordionFirstLine.style.borderColor = strokeColorConfig;
      }

      if (accordionItems.length) {
        accordionItems.forEach((node) => {
          // eslint-disable-next-line no-param-reassign
          node.style.borderColor = strokeColorConfig;
        });
      }

      const hoverNodes = accordionRef.current.querySelectorAll(
        '.editable-panel__wrapper .panel-group > div button'
      );

      if (hoverNodes.length && rollOverColorOptions) {
        accordionRef.current.classList.add('custom-accordion-style-color');
        hoverNodes.forEach((node) => {
          // eslint-disable-next-line no-param-reassign
          node.style.outlineColor = strokeColorConfig;
          // eslint-disable-next-line no-param-reassign
          accordionRef.current.style.setProperty(
            '--hover-background-color',
            rollOverColorConfig
          );
        });
      }
    }
  }, [rollOverColorConfig, strokeColorConfig, rollOverColorOptions]);

  return (
    <UniversalContainer {...container}>
      <CenteredContainer
        className="editable-panel__cont"
        style={containerWidthStyle}
      >
        <UniversalText {...title} />
        <UniversalText {...subtitle} textType="text" />
        <ElementImpression {...elementImpressionProps}>
          <Box
            className="editable-panel__wrapper"
            style={{ ...strokeColorStyle }}
            ref={accordionRef}
          >
            <Accordion
              enableTracking
              items={items || []}
              analytics={analytics}
              id={nodeId}
            />
          </Box>
        </ElementImpression>
        {outputSchema && (
          <script
            type="application/ld+json"
            // eslint-disable-next-line react/no-danger
            dangerouslySetInnerHTML={{ __html: JSON.stringify(jsonLd) }}
          />
        )}
      </CenteredContainer>
    </UniversalContainer>
  );
};

UniversalAccordion.displayName = 'Universal accordion';
UniversalAccordion.getEditingConfig = (
  componentDocument,
  fieldLabel = 'Universal accordion',
  fieldName = 'universalAccordion',
  options = {
    disableGroup: false,
    includeContainer: false,
  }
) => {
  const editorPanelOptions = [
    ...UniversalContainer.getEditingConfig(
      componentDocument,
      'Container',
      [
        'backgroundColor',
        'backgroundImage',
        'border',
        'margin',
        'padding',
        'hide',
        'jumpLinkId',
      ],
      `${fieldName}.container`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Title',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      `${fieldName}.title`
    ),
    ...UniversalText.getEditingConfig(
      componentDocument,
      'Subtitle',
      [
        'fontWeight',
        'groupButton',
        'margin',
        'padding',
        'textChronicle',
        'textColor',
        'textSize',
        'textType',
      ],
      `${fieldName}.subtitle`,
      { text: { textType: 'text' } }
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'strokeColorOptions',
      'Stroke color options'
    ),
    ...getTextColor.getEditingConfig(
      componentDocument,
      'rollOverColorOptions',
      'Roll over color options'
    ),
    boolCheckboxSpec('fullWidthMobile', '100% width in mobile viewports?', {
      help: 'The accordion defaults to a width of 280px on mobile viewports. Check this box to make it 100% width for mobile viewports instead.',
    }),
    boolCheckboxSpec(
      'dangerouslyAllowHTMLInBody',
      'Allow HTML in panel body sections?',
      {
        help: 'WARNING: We strongly advise against making use of this feature unless you really need it.',
      }
    ),
    boolCheckboxSpec('outputSchema', 'Output as FAQpage JSON schema?', {
      help: 'Check this ONLY if 1) your content is a FAQ and 2) you want to output a FAQPage rich snippet for your FAQ. Please note that to enable enhanced search results, an editor must also un-check the "Prevent Search Engines from Following Links?" checkbox in the SEO Options section of the Wordpress editor.',
    }),
    repeatable('panels', 'Accordion Sections', 'Section', (item) => [
      // stringTextAreaSpec('header', 'Header'),
      // stringTextAreaSpec('body', 'Body'),
      ...UniversalText.getEditingConfig(
        componentDocument,
        'Header',
        [
          'fontWeight',
          'groupButton',
          'margin',
          'padding',
          'textChronicle',
          'textColor',
          'textSize',
          'textType',
        ],
        `${fieldName}.panels.${item}.header`,
        { text: { textType: 'text' } }
      ),
      ...UniversalText.getEditingConfig(
        componentDocument,
        'Body',
        [
          'fontWeight',
          'groupButton',
          'margin',
          'padding',
          'textChronicle',
          'textColor',
          'textSize',
          'textType',
        ],
        `${fieldName}.panels.${item}.body`,
        { text: { textType: 'text' } }
      ),
    ]),
  ];

  return [
    options.disableGroup
      ? wrapper(editorPanelOptions)
      : group(fieldLabel, editorPanelOptions),
  ];
};

UniversalAccordion.defaultProps = {
  dangerouslyAllowHTMLInBody: false,
  fullWidthMobile: false,
  panels: [],
  titleSize: '2',
  size: '',
};

UniversalAccordion.propTypes = {
  title: PropTypes.string,
  subtitle: PropTypes.string,
  universalAccordion: PropTypes.object,
  strokeColorOptions: PropTypes.object,
  rollOverColorOptions: PropTypes.object,
  fullWidthMobile: PropTypes.bool,
  panels: PropTypes.arrayOf(
    PropTypes.shape({
      header: PropTypes.string,
      body: PropTypes.string,
    })
  ),
  dangerouslyAllowHTMLInBody: PropTypes.bool,
  size: PropTypes.string,
  outputSchema: PropTypes.bool,
};

export default UniversalAccordion;
